import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useCompetition } from '../competition/CompetitionContext';
import './SideMenu.css';

function SideMenu() {
  const navigate = useNavigate();
  const { user, logout, hasPermission, getAccessibleExternalPages } = useAuth();
  const { selectedCompetition, clearCompetition } = useCompetition();

  const handleChangeCompetition = () => {
    clearCompetition();
    navigate('/select-competition');
  };

  // Define all possible menu items
  const allMenuItems = [
    { path: '/administration', label: 'Administration', icon: '⚙️', permission: 'administration' },
    { path: '/screen-management', label: 'Screen Management', icon: '🖥️', permission: 'screen-management' },
    { path: '/payments', label: 'Payments', icon: '💰', permission: 'administration' },
    { path: '/scrutineer', label: 'Scrutineer', icon: '✓', permission: 'scrutineer' },
    { path: '/compere', label: 'Compere', icon: '🎤', permission: 'compere' }
  ];

  // Filter menu items based on user permissions
  const menuItems = allMenuItems.filter(item => hasPermission(item.permission));

  // Get accessible external pages based on user role
  const accessibleExternalPages = getAccessibleExternalPages();

  const handleRunningListClick = () => {
    // Open Running List in new window with currently selected competition ID
    if (selectedCompetition) {
      const url = `${window.location.origin}/${selectedCompetition.id}`;
      window.open(url, '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
    }
  };

  const handleMarshallAreaClick = () => {
    // Open Marshall Area in new window with mobile-friendly dimensions
    if (selectedCompetition) {
      const url = `${window.location.origin}/marshall-area/${selectedCompetition.id}`;
      // Check if device is mobile and use appropriate dimensions
      const isMobile = window.innerWidth <= 768;
      const width = isMobile ? window.innerWidth : Math.min(1200, window.innerWidth - 100);
      const height = isMobile ? window.innerHeight : Math.min(800, window.innerHeight - 100);
      const left = isMobile ? 0 : (window.innerWidth - width) / 2;
      const top = isMobile ? 0 : (window.innerHeight - height) / 2;

      window.open(
        url,
        '_blank',
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes,location=yes,menubar=no`
      );
    }
  };

  const handleChairAdjudicatorClick = () => {
    // Open Chair Adjudicator in new window with mobile-friendly dimensions
    if (selectedCompetition) {
      const url = `${window.location.origin}/chair-adjudicator/${selectedCompetition.id}`;
      // Check if device is mobile and use appropriate dimensions
      const isMobile = window.innerWidth <= 768;
      const width = isMobile ? window.innerWidth : Math.min(1200, window.innerWidth - 100);
      const height = isMobile ? window.innerHeight : Math.min(800, window.innerHeight - 100);
      const left = isMobile ? 0 : (window.innerWidth - width) / 2;
      const top = isMobile ? 0 : (window.innerHeight - height) / 2;

      window.open(
        url,
        '_blank',
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes,location=yes,menubar=no`
      );
    }
  };

  return (
    <div className="side-menu">
      <div className="side-menu-header">
        <button className="mobile-menu-toggle" onClick={() => {}}>
          ☰
        </button>
        <h2>Flokraft Ultimate</h2>
        <p className="app-subtitle">DanceSport Management System</p>
      </div>

      {selectedCompetition && (
        <div className="current-competition">
          <p className="competition-label">Current Competition</p>
          <p className="competition-name">{selectedCompetition.name}</p>
        </div>
      )}

      <div className="mobile-current-competition">
        {selectedCompetition && (
          <p className="mobile-competition-name">{selectedCompetition.name}</p>
        )}
      </div>

      <nav className="side-menu-nav">
        {menuItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              isActive ? 'menu-item active' : 'menu-item'
            }
            title={item.label}
          >
            <span className="nav-icon">{item.icon}</span>
            <span className="nav-label">{item.label}</span>
          </NavLink>
        ))}

        {/* External Pages - Render based on permissions */}
        {accessibleExternalPages.some(page => page.key === 'running-list') && (
          <button
            className="menu-item running-list-item"
            onClick={handleRunningListClick}
            title="Open Running List in new window"
          >
            <span className="nav-icon">📝</span>
            <span className="nav-label">Running List</span>
            <span className="public-indicator">🌐</span>
          </button>
        )}

        {accessibleExternalPages.some(page => page.key === 'marshall-area') && (
          <button
            className="menu-item marshall-area-item"
            onClick={handleMarshallAreaClick}
            title="Open Marshall Area in new window"
          >
            <span className="nav-icon">📋</span>
            <span className="nav-label">Marshall Area</span>
            <span className="public-indicator">🌐</span>
          </button>
        )}

        {accessibleExternalPages.some(page => page.key === 'chair-adjudicator') && (
          <button
            className="menu-item chair-adjudicator-item"
            onClick={handleChairAdjudicatorClick}
            title="Open Chair Adjudicator in new window"
          >
            <span className="nav-icon">👥</span>
            <span className="nav-label">Chair Adjudicator</span>
            <span className="public-indicator">🌐</span>
          </button>
        )}

        {/* Audit Trail - System Monitoring - Only for Master */}
        {hasPermission('audit-trail') && (
          <NavLink
            to="/audit-trail"
            className={({ isActive }) =>
              isActive ? 'menu-item audit-trail-item active' : 'menu-item audit-trail-item'
            }
            title="View system audit trail"
          >
            <span className="nav-icon">📊</span>
            <span className="nav-label">Audit Trail</span>
          </NavLink>
        )}
      </nav>

      <div className="side-menu-footer">
        {user && (
          <div className="user-info">
            <div className="user-details">
              <p className="user-name">{user.username.charAt(0).toUpperCase() + user.username.slice(1)}</p>
              <p className="user-role">{user.type?.toUpperCase() + ' USER' || 'USER'}</p>
            </div>
            {selectedCompetition && (
              <button onClick={handleChangeCompetition} className="change-competition-button">
                Change Competition
              </button>
            )}
            <button onClick={logout} className="logout-button">
              Logout
            </button>
          </div>
        )}
        <p className="version-info">v1.0.0</p>
      </div>
    </div>
  );
}

export default SideMenu;
